function cleanText(value) {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function normalize(value) {
  return cleanText(value).toLowerCase();
}

function hasAny(text, patterns) {
  return patterns.some((pattern) =>
    pattern instanceof RegExp ? pattern.test(text) : text.includes(pattern)
  );
}

const THANKS_PATTERNS = [
  /\b(thanks|thank you|thank u|ta|cheers|appreciate it)\b/i,
  /\b(okay|ok|great|noted|alright|sure)\b.{0,20}\b(thanks|thank you)\b/i,
  /^(okay|ok|great|noted|alright|sure)[.! ]*$/i,
];

const PRESCRIPTION_STATUS_PATTERNS = [
  /\b(status|update|ready|collect|pickup|pick up|pharmacy)\b/i,
  /\b(what is happening|what's happening|where is)\b.{0,40}\b(prescription|medicine|medication|rx)\b/i,
  /\b(prescription|medicine|medication|rx)\b.{0,40}\b(status|ready|update|pharmacy)\b/i,
];

const APPOINTMENT_NOTES_PATTERNS = [
  /\b(doctor'?s|clinician'?s|consultation|appointment|visit|my)\b.{0,40}\b(notes?|record|ehr)\b/i,
  /\b(i don'?t understand|do not understand|what does this mean|help me understand)\b.{0,60}\b(notes?|record|appointment|consultation|ehr)\b/i,
  /\b(notes?|record)\b.{0,60}\b(i don'?t understand|do not understand|what does this mean|means?)\b/i,
];

const APPOINTMENT_HELP_PATTERNS = [
  /\b(appointment|consultation|visit|booking|e-visit|evisit|telehealth)\b/i,
  /\b(next|last|latest|upcoming|previous)\b.{0,40}\b(doctor|clinician|appointment|consultation|visit)\b/i,
  /\b(when|what time|where|who)\b.{0,50}\b(appointment|consultation|visit|doctor|clinician)\b/i,
  /\b(approved|approval|pending|cancel|cancelled|canceled|reschedule|missed)\b.{0,50}\b(appointment|consultation|visit|booking)\b/i,
  /\b(do i have any appointments|show me my latest appointment|who is my doctor)\b/i,
];

const CARE_NAVIGATION_PATTERNS = [
  /\b(i need|need to|want to|can i)\b.{0,40}\b(see|book|visit|speak to|talk to)\b.{0,30}\b(doctor|clinician|nurse)\b/i,
  /\b(book|booking|appointment|doctor|clinician|e-visit|evisit|nearby doctor|find a doctor)\b/i,
  /\bwhat should i do next\b/i,
];

const SYMPTOM_PATTERNS = [
  /\b(i feel|feeling|i have|having|symptom|symptoms|pain|ache|fever|dizzy|dizziness|hot flush|cold|cough|rash|vomit|nausea|headache|unwell|sick)\b/i,
  /\bshould i be worried\b/i,
];

const URGENT_PATTERNS = [
  /\b(urgent|emergency|severe|chest pain|cannot breathe|can't breathe|difficulty breathing|trouble breathing|fainted|fainting|seizure|severe bleeding|suicidal|suicide|overdose|swollen tongue|swollen throat)\b/i,
];

export function classifyFolaSupportIntent(message) {
  const text = cleanText(message);
  if (!text) return "unknown";

  if (hasAny(text, THANKS_PATTERNS)) return "thanks";
  if (hasAny(text, PRESCRIPTION_STATUS_PATTERNS) && /\b(prescription|medicine|medication|rx|pharmacy|status|update|ready)\b/i.test(text)) {
    return "prescription_status";
  }
  if (hasAny(text, APPOINTMENT_NOTES_PATTERNS)) return "appointment_notes";
  if (hasAny(text, APPOINTMENT_HELP_PATTERNS)) return "appointment_help";
  if (hasAny(text, URGENT_PATTERNS)) return "urgent_care_navigation";
  if (hasAny(text, CARE_NAVIGATION_PATTERNS)) return "care_navigation";
  if (hasAny(text, SYMPTOM_PATTERNS)) return "symptom";

  return "unknown";
}

export function resolveFirstName(context = {}) {
  const direct = cleanText(context.firstName || context.first_name);
  if (direct) return direct;
  const display = cleanText(context.displayName || context.display_name || context.patientName || context.name);
  if (!display) return "";
  return display.split(/\s+/)[0] || "";
}

export function formatReference(prescription = {}) {
  return cleanText(
    prescription.reference ||
      prescription.external_rx_id ||
      prescription.rxNo ||
      prescription.rx_no ||
      prescription.id
  );
}

export function humanizeStatus(status) {
  const raw = normalize(status);
  if (!raw) return "";
  const map = {
    issued: "issued",
    active: "active",
    pending: "pending",
    draft: "draft",
    cancelled: "cancelled",
    rejected: "rejected",
    pharmacy_selected: "sent to the selected pharmacy",
    preparing: "being prepared by the pharmacy",
    awaiting_payment: "awaiting payment",
    priced: "priced by the pharmacy",
    ready_for_pickup: "ready for pickup",
    ready: "ready",
    paid: "paid",
    payment_confirmed: "payment confirmed",
    pending_selection: "waiting for pharmacy selection",
    not_started: "not started",
  };
  return map[raw] || raw.replace(/_/g, " ");
}

export function buildPrescriptionStatusReply(context = {}) {
  const firstName = resolveFirstName(context);
  const namePrefix = firstName ? `${firstName}, ` : "";
  const prescription = context.prescription || context.latestPrescription || null;
  const fulfillment = context.fulfillment || context.pharmacy || null;

  if (!prescription) {
    return `${namePrefix}I can't find an active prescription linked to your account right now. You can check your appointment details or ask your clinician if one was issued.`;
  }

  const reference = formatReference(prescription);
  const prescriptionStatus = humanizeStatus(prescription.status);
  const fulfillmentStatus = humanizeStatus(fulfillment?.status);
  const pharmacyName = cleanText(fulfillment?.pharmacyName || fulfillment?.pharmacy_name);

  const referenceText = reference ? ` ${reference}` : "";
  const statusText = prescriptionStatus
    ? ` Its current prescription status is ${prescriptionStatus}.`
    : " I can see the prescription record, but I don't have a clear prescription status label yet.";

  let pharmacyText = "";
  if (fulfillmentStatus) {
    pharmacyText = pharmacyName
      ? ` The latest pharmacy update from ${pharmacyName} is ${fulfillmentStatus}.`
      : ` The latest pharmacy update is ${fulfillmentStatus}.`;
  } else {
    pharmacyText = " I can see this is a staging workflow, but I don't yet have a live pharmacy update for this prescription.";
  }

  return `${namePrefix}I found your latest prescription${referenceText}.${statusText}${pharmacyText} I can also help you check the related appointment or pharmacy status.`;
}

export function buildThanksReply() {
  return "You're welcome. Is there anything else I can help you with?";
}

export function formatAppointmentDate(value) {
  const raw = cleanText(value);
  if (!raw) return "";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
}

export function formatAppointmentDateTime(value) {
  const raw = cleanText(value);
  if (!raw) return "";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return new Intl.DateTimeFormat("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

export function humanizeAppointmentStatus(status, reason = "") {
  const raw = normalize(status);
  const rawReason = normalize(reason);
  if (raw === "pending") return "pending clinician approval";
  if (raw === "scheduled") return "scheduled";
  if (raw === "confirmed") return "confirmed";
  if (raw === "in_progress") return "in progress";
  if (raw === "completed") return "completed";
  if (raw === "missed") return "missed";
  if (raw === "cancelled" && rawReason === "declined") return "cancelled after being declined";
  if (raw === "cancelled" && rawReason === "rescheduled") return "cancelled because it was rescheduled";
  if (raw === "cancelled") return "cancelled";
  return raw ? raw.replace(/_/g, " ") : "";
}

export function describeAppointmentMode(appointment = {}) {
  const type = normalize(appointment.consultationType || appointment.consultation_type);
  const videoStatus = normalize(appointment.videoStatus || appointment.video_status);
  if (["private", "virtual", "telehealth", "e-visit", "evisit"].includes(type)) {
    return videoStatus ? `a virtual consultation with video status ${videoStatus.replace(/_/g, " ")}` : "a virtual consultation";
  }
  if (["moh", "physical", "in-person", "inperson"].includes(type)) return "a physical visit";
  return type ? type.replace(/_/g, " ") : "";
}

export function describeAppointment(appointment = {}) {
  const doctorName = cleanText(appointment.doctorName || appointment.doctor_name || appointment.clinicianName);
  const specialty = cleanText(appointment.specialty || appointment.primarySpecialty || appointment.specialisation);
  const dateTime = formatAppointmentDateTime(appointment.appointmentDate || appointment.appointment_date);
  const status = humanizeAppointmentStatus(appointment.status, appointment.statusReason || appointment.status_reason);
  const mode = describeAppointmentMode(appointment);
  const facility = cleanText(appointment.facilityName || appointment.facility_name);
  const address = cleanText(appointment.facilityAddress || appointment.facility_address || appointment.location);

  const parts = [];
  if (doctorName) parts.push(`with ${doctorName}${specialty ? ` (${specialty})` : ""}`);
  if (dateTime) parts.push(`on ${dateTime}`);
  if (status) parts.push(`currently marked as ${status}`);
  if (mode) parts.push(`as ${mode}`);
  if (facility || address) parts.push(`at ${[facility, address].filter(Boolean).join(", ")}`);
  return parts.length ? parts.join(", ") : "";
}

function hasLocationContext(context = {}) {
  return Boolean(cleanText(
    context.currentArea ||
      context.current_area ||
      context.preferredArea ||
      context.preferred_area ||
      context.location ||
      context.city ||
      context.country
  ));
}

export function classifyAppointmentHelpFocus(message) {
  const text = normalize(message);
  if (/\b(cancel|cancelled|canceled|cancellation)\b/.test(text)) return "cancellation";
  if (/\b(reschedule|change time|change date|move my appointment)\b/.test(text)) return "reschedule";
  if (/\b(approved|approval|pending)\b/.test(text)) return "approval";
  if (/\b(where|location|address|place|facility)\b/.test(text)) return "location";
  if (/\b(who|doctor|clinician)\b/.test(text)) return "doctor";
  if (/\b(e-visit|evisit|telehealth|video|online|virtual)\b/.test(text)) return "mode";
  if (/\b(last|latest|previous|what happened|missed)\b/.test(text)) return "latest";
  if (/\b(when|what time|next|upcoming)\b/.test(text)) return "next";
  return "summary";
}

export function buildAppointmentHelpReply(context = {}) {
  const focus = context.appointmentFocus || classifyAppointmentHelpFocus(context.message);
  const summary = context.appointments || {};
  const next = summary.nextAppointment || context.nextAppointment || context.appointment || null;
  const latest = summary.latestAppointment || context.latestAppointment || context.appointment || null;
  const completed = summary.latestCompletedAppointment || null;
  const pending = summary.pendingAppointment || null;
  const cancelled = summary.cancelledAppointment || null;
  const cancellationRequest = summary.cancellationRequest || null;
  const rescheduleRequest = summary.rescheduleRequest || null;
  const target = focus === "latest"
    ? latest || completed || cancelled
    : focus === "cancellation" || focus === "reschedule"
      ? next || pending || latest
    : focus === "approval"
      ? pending || next || latest
      : next || pending || latest;

  const nextSteps = "I can also help you view appointment details, book an appointment, find a doctor, message the clinician, request cancellation or rescheduling, or open myEHR.";

  if (!target) {
    const locationText = hasLocationContext(context)
      ? " I can use your current area to help find nearby clinicians."
      : " I may need your location or preferred area first so I can guide you to nearby options.";
    const urgentText = hasAny(cleanText(context.message), URGENT_PATTERNS)
      ? " If this feels urgent or severe, please seek immediate medical help or emergency care."
      : "";
    return `I can't find an active appointment linked to your account right now. If you're not feeling well, I can help you find a nearby clinician or start booking a virtual consultation through AfriMed™.${locationText}${urgentText}`;
  }

  const description = describeAppointment(target);
  const prefix = description ? `I found ${focus === "latest" ? "your latest appointment" : "your appointment"} ${description}.` : "I found an appointment linked to your account, but some details are unavailable.";

  if (focus === "cancellation") {
    if (cancellationRequest?.state === "requested") {
      return `${prefix} I can see your appointment cancellation request is still pending clinician approval. The appointment should not be treated as cancelled until its status changes. ${nextSteps}`;
    }
    if (normalize(target.status) === "cancelled") {
      return `${prefix} It is already marked as cancelled. ${nextSteps}`;
    }
    return `${prefix} I can help you request cancellation, but I cannot mark it as cancelled unless the appointment status has actually changed. ${nextSteps}`;
  }

  if (focus === "reschedule") {
    if (rescheduleRequest?.state === "requested") {
      return `${prefix} I can see a reschedule request is pending. I do not see it marked as applied yet. ${nextSteps}`;
    }
    return `${prefix} I can help you request a reschedule, but I will not treat the time as changed until the appointment record updates. ${nextSteps}`;
  }

  if (focus === "approval") {
    const status = humanizeAppointmentStatus(target.status, target.statusReason || target.status_reason);
    return `${prefix} Its approval/status is ${status || "not clearly labelled yet"}. ${nextSteps}`;
  }

  if (focus === "location") {
    const facility = cleanText(target.facilityName || target.facility_name);
    const address = cleanText(target.facilityAddress || target.facility_address || target.location);
    if (!facility && !address) {
      return `${prefix} I don't see a physical location saved for it yet. If it is a virtual consultation, check the appointment details for the video link/status. ${nextSteps}`;
    }
    return `${prefix} The saved location is ${[facility, address].filter(Boolean).join(", ")}. ${nextSteps}`;
  }

  if (focus === "doctor") {
    const doctorName = cleanText(target.doctorName || target.doctor_name || target.clinicianName);
    if (!doctorName) return `${prefix} I don't see a clinician name saved on this appointment yet. ${nextSteps}`;
    return `${prefix} Your clinician is ${doctorName}. ${nextSteps}`;
  }

  if (focus === "mode") {
    const mode = describeAppointmentMode(target);
    return `${prefix} It is ${mode || "not clearly labelled as a virtual consultation or physical visit yet"}. ${nextSteps}`;
  }

  return `${prefix} ${nextSteps}`;
}

export function pickPatientVisibleAppointmentNote(appointment = {}) {
  const title = cleanText(appointment.diagnosisTitle || appointment.diagnosis_title);
  const diagnosisNotes = cleanText(appointment.diagnosisNotes || appointment.diagnosis_notes);
  const notes = cleanText(appointment.notes || appointment.patientNotes || appointment.patient_notes);

  if (title && diagnosisNotes) return `${title}: ${diagnosisNotes}`;
  return diagnosisNotes || notes || title;
}

export function buildAppointmentNotesReply(context = {}) {
  const appointment = context.appointment || context.latestAppointment || null;
  if (!appointment) {
    return "I can't find a previous completed appointment linked to your account yet.";
  }

  const doctorName = cleanText(appointment.doctorName || appointment.doctor_name || appointment.clinicianName);
  const specialty = cleanText(appointment.specialty || appointment.primarySpecialty || appointment.specialisation);
  const appointmentDate = formatAppointmentDate(appointment.appointmentDate || appointment.appointment_date);
  const note = pickPatientVisibleAppointmentNote(appointment);

  const doctorText = doctorName ? ` with ${doctorName}` : "";
  const specialtyText = specialty ? ` (${specialty})` : "";
  const dateText = appointmentDate ? ` on ${appointmentDate}` : "";
  const appointmentText = `I found your last appointment${doctorText}${specialtyText}${dateText}`;

  if (!note) {
    return `${appointmentText}, but I don't see patient-visible consultation notes available to explain yet. I can help you view the appointment details, message the clinician, book a follow-up, or open myEHR.`;
  }

  return `${appointmentText}. The patient-visible note says: "${note}". In plain language, this appears to be the clinician's recorded wording for that visit. Please treat this as a clarification of the wording, not a new diagnosis or treatment instruction. If anything feels unclear or worrying, I can help you message the clinician, book a follow-up, view the appointment details, or open myEHR.`;
}

export function buildCareNavigationReply({ urgent = false } = {}) {
  const urgentText = urgent
    ? "If this feels urgent or severe, please seek immediate medical care or contact local emergency services."
    : "If symptoms feel severe, fast-changing, or urgent, please seek immediate medical care.";
  return `I can't diagnose this in chat, but I can help you take the next operational step. I can help you find a clinician, book an appointment, view your appointments, or start a virtual consultation where available. ${urgentText}`;
}

export function buildSymptomReply() {
  return "I can't diagnose symptoms in chat. A healthcare professional should review this, especially if it is new, worsening, or worrying. I can help you find a nearby doctor, book an appointment, or start a virtual consultation where available. If it feels urgent, please seek immediate medical care.";
}

export function buildFolaGroundedSupportReply(intent, context = {}) {
  switch (intent) {
    case "thanks":
      return buildThanksReply();
    case "prescription_status":
      return buildPrescriptionStatusReply(context);
    case "appointment_help":
      return buildAppointmentHelpReply(context);
    case "appointment_notes":
      return buildAppointmentNotesReply(context);
    case "urgent_care_navigation":
      return buildCareNavigationReply({ urgent: true });
    case "care_navigation":
      return buildCareNavigationReply();
    case "symptom":
      return buildSymptomReply();
    default:
      return "";
  }
}

export function resolveGroundedFolaReply(message, context = {}) {
  const intent = classifyFolaSupportIntent(message);
  const reply = buildFolaGroundedSupportReply(intent, { ...context, message });
  return reply ? { intent, reply } : null;
}

export const folaGroundingInternals = {
  cleanText,
  normalize,
};
